import { useSelectAppMode } from 'app/store/general/selectors';
import { usePortfolioSelectedSymbols } from 'portfolio/store/hooks';
import { AppMode } from 'app/store/general/AppMode';
import { portfolioMessage } from 'portfolio/components/SelectionStatus';
import { useCorporateActionsData } from 'widgets/corpActions/store/data/hooks';
import { StreamMessage } from 'utils/messages/StreamMessage';
import { useSelectCurrentCurrency } from 'app/store/selectors';
import { useEffect, useMemo, useState } from 'react';
import { buildFiltersFunction } from 'utils/filter/functions/filtersFunction';
import { corporateActionsColumnDefinitionsDictionary } from 'widgets/corpActions/widget/grid/columnDefinitions';
import { SymbolEvent } from 'widgets/corpActions/data/SymbolEvent';
import { useCorporateActionsConfigSlice } from 'widgets/corpActions/widget/store/config/configSlice';
import cloneDeep from 'lodash/cloneDeep';
import { ValueType } from '@instinet-analytics/semantic-ui-components';
import { ExactSign } from 'utils/filter/filter';

import { useEarningsAftMktEnricher } from 'widgets/corpActions/store/data/earningsAftMktOnTMinus1.ts';

const WidgetTitle = 'Events Widget';

const meetsRequirements = { meetsRequirements: true };
const standalone = {
    meetsRequirements: false,
    alert: `Select at least one symbol to use the ${WidgetTitle}`,
};
const newport = {
    meetsRequirements: false,
    alert: portfolioMessage(WidgetTitle),
};

export function useCorporateActionsPortfolioRequirements(): {
    meetsRequirements: boolean;
    alert?: string;
} {
    const appMode = useSelectAppMode();
    const symbols = usePortfolioSelectedSymbols();

    if (symbols.length > 0) {
        return meetsRequirements;
    }
    return appMode === AppMode.STANDALONE ? standalone : newport;
}

export function useCorporateActionsAlerts(): StreamMessage[] {
    const data = useCorporateActionsData();

    const ret: StreamMessage[] = [];
    if (data?.errors) {
        for (const error of data.errors) {
            const msg = (error as { message: string })['message'] ?? undefined;
            ret.push({
                message: msg ?? 'Error retrieving data',
            });
        }
    }

    if ((data?.missingSymbols ?? []).length > 0) {
        return [
            {
                message:
                    'The following symbol(s) could not be resolved to a Bloomberg symbol: ' +
                    data!.missingSymbols!.join(', '),
            },
        ];
    }

    return ret;
}

export const useUnFilteredCorporateActionsData = () => {
    const dataState = useCorporateActionsData();
    return useMemo(() => dataState?.events ?? [], [dataState]);
};

export const useFilteredCorporateActionsData = () => {
    const data = useUnFilteredCorporateActionsData();
    const currency = useSelectCurrentCurrency();
    const { excludeToday, excludeHistorical, excludeUpcoming, filters } =
        useCorporateActionsConfigSlice().config;

    const [filteredData, setFilteredData] = useState<SymbolEvent[]>([]);

    const { enrichedSymbolEvents } = useEarningsAftMktEnricher(filteredData);

    useEffect(() => {
        if (!data) {
            setFilteredData([]);
            return;
        }
        const adornedFilters = cloneDeep(filters);
        if ((!excludeHistorical || !excludeUpcoming) && excludeToday) {
            // If today is unchecked and either hist or full is checked, we hide today...
            adornedFilters.push({
                column: 'effectiveDate',
                filter: {
                    valueType: ValueType.DATE,
                    exactSign: ExactSign.NEQ,
                    value: 'Today',
                },
            });
        }
        const filterFn = buildFiltersFunction(
            corporateActionsColumnDefinitionsDictionary,
            adornedFilters ?? [],
            currency?.rate ?? 1
        );

        const nextFilteredData = data.filter(filterFn);
        setFilteredData(nextFilteredData);
    }, [
        data,
        filters,
        currency,
        excludeToday,
        excludeHistorical,
        excludeUpcoming,
    ]);

    return enrichedSymbolEvents;
};
